import { Fragment, useEffect, useMemo, useState } from 'react';
import { apiService } from '../../services/api.service';
import { formatDateTimeAr, formatMoneyAr } from './loan-financial.format';
import { SectionCard, SkeletonBlock } from './LoanUiBits';
import { JournalEntryDrawer } from '../accounting/books/JournalEntryDrawer';

type Props = {
  loanId: string;
  legalEntityId?: string | null;
  loanCreatedAt?: string | Date | null;
  enabled: boolean;
};

export function LoanJournalTable({ loanId, legalEntityId, loanCreatedAt, enabled }: Props) {
  const [entries, setEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<any | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const range = useMemo(() => {
    const to = new Date();
    const from = loanCreatedAt ? new Date(loanCreatedAt) : new Date(to.getFullYear() - 2, 0, 1);
    const iso = (d: Date) => d.toISOString().slice(0, 10);
    return { dateFrom: iso(from), dateTo: iso(to) };
  }, [loanCreatedAt]);

  useEffect(() => {
    if (!enabled || !legalEntityId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await apiService.getAccountingDailyBook({
          legalEntityId,
          dateFrom: range.dateFrom,
          dateTo: range.dateTo,
          q: loanId,
          includeReversed: true,
          take: 50,
          skip: 0,
        });
        if (!cancelled) setEntries(data.entries ?? []);
      } catch {
        if (!cancelled) {
          setError('No se pudieron cargar los movimientos contables.');
          setEntries([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [enabled, legalEntityId, loanId, range.dateFrom, range.dateTo]);

  if (!enabled) return null;

  return (
    <SectionCard title="Movimientos contables" subtitle="Asientos relacionados al préstamo (paginado).">
      {!legalEntityId ? (
        <p className="text-sm text-amber-800">No hay empresa asociada para consultar el diario.</p>
      ) : loading ? (
        <SkeletonBlock className="h-32" />
      ) : error ? (
        <p className="text-sm text-amber-800">{error}</p>
      ) : entries.length === 0 ? (
        <p className="text-sm text-slate-500">Sin asientos encontrados para este préstamo en el período.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-2 py-2">Fecha</th>
                <th className="px-2 py-2">Evento</th>
                <th className="px-2 py-2">Referencia</th>
                <th className="px-2 py-2 text-right">Debe</th>
                <th className="px-2 py-2 text-right">Haber</th>
                <th className="px-2 py-2">Estado</th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {entries.map((e) => {
                const reversed = e.status === 'REVERSED';
                const open = expandedId === e.id;
                return (
                  <Fragment key={e.id}>
                    <tr className="hover:bg-slate-50">
                      <td className="px-2 py-2">{formatDateTimeAr(e.entryDate)}</td>
                      <td className="px-2 py-2">{e.description ?? e.sourceType ?? '—'}</td>
                      <td className="px-2 py-2 text-xs text-slate-600">
                        #{e.entryNumber ?? '—'}
                        {reversed ? (
                          <span className="ml-2 inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-600">
                            REVERTIDO
                          </span>
                        ) : null}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums">
                        {formatMoneyAr(e.totalDebit)}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums">
                        {formatMoneyAr(e.totalCredit)}
                      </td>
                      <td className="px-2 py-2 text-xs">{e.status}</td>
                      <td className="px-2 py-2 text-right space-x-2">
                        <button
                          type="button"
                          className="text-xs text-blue-700 hover:underline"
                          onClick={() => setExpandedId(open ? null : e.id)}
                        >
                          {open ? 'Ocultar' : 'Líneas'}
                        </button>
                        <button
                          type="button"
                          className="text-xs text-blue-700 hover:underline"
                          onClick={async () => {
                            try {
                              const full = await apiService.getAccountingEntry(e.id);
                              setSelected(full);
                            } catch {
                              setSelected(e);
                            }
                          }}
                        >
                          Ver
                        </button>
                      </td>
                    </tr>
                    {open ? (
                      <tr className="bg-slate-50">
                        <td colSpan={7} className="px-3 py-2">
                          <table className="min-w-full text-xs">
                            <thead>
                              <tr className="text-left text-slate-500">
                                <th className="pr-2 py-1">Código</th>
                                <th className="pr-2 py-1">Cuenta</th>
                                <th className="pr-2 py-1 text-right">Debe</th>
                                <th className="py-1 text-right">Haber</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(e.lines ?? []).map((l: any) => (
                                <tr key={l.id} className="border-t border-slate-200">
                                  <td className="pr-2 py-1">{l.account?.code ?? '—'}</td>
                                  <td className="pr-2 py-1">{l.account?.name ?? '—'}</td>
                                  <td className="pr-2 py-1 text-right tabular-nums">
                                    {formatMoneyAr(l.debit)}
                                  </td>
                                  <td className="py-1 text-right tabular-nums">
                                    {formatMoneyAr(l.credit)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <JournalEntryDrawer entry={selected} onClose={() => setSelected(null)} />
    </SectionCard>
  );
}
